import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

async function getCheckoutFlowModule() {
  const script = document.querySelector('script[src*="conv-checkout-flow"]');
  if (!script?.src) throw new Error('conv-checkout-flow script missing');
  return import(script.src);
}

class CartCrossSell extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#load();
    document.addEventListener('cart:update', () => this.#load());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target.closest('.cart-cross-sell__add');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    this.#addToCart(button);
  };

  async #load() {
    if (document.readyState === 'loading') {
      await new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      });
    }

    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;

    try {
      const flow = await getCheckoutFlowModule();
      const cartMeta = await this.#getCartMeta(signal);

      if (!flow.hasCartDrawerUpsell(cartMeta.ids)) {
        this.innerHTML = '';
        return;
      }

      const html = await flow.fetchUpsellForCart(cartMeta.handles, cartMeta.ids, 'cart', signal);
      this.innerHTML = html || '';
    } catch (error) {
      console.warn('Cart cross-sell load:', error);
      this.innerHTML = '';
    }
  }

  async #getCartMeta(signal) {
    try {
      const res = await fetch('/cart.js', { signal });
      if (!res.ok) throw new Error('cart unavailable');
      const cart = await res.json();
      return {
        handles: cart.items.map((item) => item.handle).join(','),
        ids: cart.items.map((item) => String(item.product_id)).join(','),
      };
    } catch {
      return {
        handles: this.dataset.cartHandles || '',
        ids: this.dataset.cartIds || '',
      };
    }
  }

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent?.trim() || 'Ekle';
    button.disabled = true;
    button.textContent = '...';

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        button.textContent = 'Hata';
        window.setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
        return;
      }

      button.textContent = 'Eklendi';
      button.classList.add('is-added');

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-cross-sell',
          itemCount: 1,
          productId: button.dataset.productId,
          sections: data.sections,
        })
      );

      window.setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('is-added');
      }, 1500);
    } catch (error) {
      console.warn('Cart cross-sell add:', error);
      button.textContent = originalText;
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
